import { useState, useEffect, useRef, useCallback } from 'react'
import axios from 'axios'

const API = process.env.REACT_APP_API_URL || ''
const INTERVALO_MS = 30_000          // cada cuánto se consulta la API
const CLAVE_PREF = 'escandella_avisos' // localStorage: 'on' | 'off'
const TITULO_BASE = 'El Rincón de la Escandella — Alicante'

/* ---------- Sonido (generado con Web Audio, sin ficheros) ---------- */
let audioCtx = null

function desbloquearAudio() {
  try {
    if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)()
    if (audioCtx.state === 'suspended') audioCtx.resume()
  } catch { /* navegador sin Web Audio */ }
}

function sonarAviso() {
  try {
    if (!audioCtx) return
    const ahora = audioCtx.currentTime
    // Dos notas cortas (ding-dong)
    ;[[880, 0], [1175, 0.18]].forEach(([freq, t]) => {
      const osc = audioCtx.createOscillator()
      const gain = audioCtx.createGain()
      osc.type = 'sine'
      osc.frequency.value = freq
      gain.gain.setValueAtTime(0.0001, ahora + t)
      gain.gain.exponentialRampToValueAtTime(0.25, ahora + t + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, ahora + t + 0.35)
      osc.connect(gain).connect(audioCtx.destination)
      osc.start(ahora + t)
      osc.stop(ahora + t + 0.4)
    })
  } catch { /* silencio */ }
}

/* ---------- Notificación del navegador ---------- */
function notificar(reservas) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return
  try {
    const r = reservas[0]
    const fecha = new Date(r.fecha + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })
    const titulo = reservas.length === 1 ? 'Nueva reserva' : `${reservas.length} reservas nuevas`
    const cuerpo = reservas.length === 1
      ? `${r.nombre_cliente} · ${fecha} ${r.hora_inicio || r.hora} · ${r.personas} pers. · ${r.ubicacion}`
      : reservas.map(x => `${x.nombre_cliente} · ${x.hora_inicio || x.hora}`).join('\n')
    const n = new Notification(titulo, { body: cuerpo, tag: 'escandella-reserva', renotify: true })
    n.onclick = () => { window.focus(); n.close() }
  } catch { /* algunos navegadores móviles no permiten new Notification() */ }
}

/* ---------- Hook ---------- */
/**
 * Consulta las reservas pendientes cada 30 s y, cuando aparecen nuevas:
 * suena un aviso, lanza una notificación del navegador, marca el título de la
 * pestaña y llama a onNuevas(reservasNuevas) para que el panel se actualice.
 */
export default function useAvisosReservas(token, onNuevas) {
  const [activos, setActivos] = useState(() => {
    try { return localStorage.getItem(CLAVE_PREF) === 'on' } catch { return false }
  })
  const [nuevasSinVer, setNuevasSinVer] = useState(0)
  const [permisoNotif, setPermisoNotif] = useState(
    'Notification' in window ? Notification.permission : 'unsupported'
  )
  const idsConocidos = useRef(null)      // null = todavía no se ha hecho la primera carga
  const onNuevasRef = useRef(onNuevas)
  useEffect(() => { onNuevasRef.current = onNuevas }, [onNuevas])

  // Título de la pestaña con contador
  useEffect(() => {
    document.title = nuevasSinVer > 0 ? `(${nuevasSinVer}) Nueva reserva — Escandella` : TITULO_BASE
    return () => { document.title = TITULO_BASE }
  }, [nuevasSinVer])

  // Al volver a la pestaña, se da por visto
  useEffect(() => {
    const limpiar = () => { if (document.visibilityState === 'visible') setNuevasSinVer(0) }
    document.addEventListener('visibilitychange', limpiar)
    window.addEventListener('focus', limpiar)
    return () => {
      document.removeEventListener('visibilitychange', limpiar)
      window.removeEventListener('focus', limpiar)
    }
  }, [])

  const comprobar = useCallback(async () => {
    if (!token) return
    try {
      const res = await axios.get(`${API}/api/admin/reservas`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { estado: 'pendiente', per_page: 100 },
      })
      const pendientes = res.data.reservas || []
      const idsAhora = new Set(pendientes.map(r => r.id))

      if (idsConocidos.current === null) {
        idsConocidos.current = idsAhora     // primera carga: no avisar de lo que ya estaba
        return
      }
      const nuevas = pendientes.filter(r => !idsConocidos.current.has(r.id))
      idsConocidos.current = idsAhora
      if (nuevas.length === 0) return

      if (activos) {
        sonarAviso()
        notificar(nuevas)
      }
      if (document.visibilityState !== 'visible') setNuevasSinVer(n => n + nuevas.length)
      onNuevasRef.current?.(nuevas)
    } catch { /* sin red o token caducado: se reintenta en el siguiente ciclo */ }
  }, [token, activos])

  // Polling
  useEffect(() => {
    comprobar()
    const id = setInterval(comprobar, INTERVALO_MS)
    return () => clearInterval(id)
  }, [comprobar])

  // Botón "Activar avisos": desbloquea audio y pide permiso de notificaciones (requiere clic del usuario)
  const activar = useCallback(async () => {
    desbloquearAudio()
    if ('Notification' in window && Notification.permission === 'default') {
      try {
        const p = await Notification.requestPermission()
        setPermisoNotif(p)
      } catch { /* ignorar */ }
    }
    setActivos(true)
    try { localStorage.setItem(CLAVE_PREF, 'on') } catch { /* ignorar */ }
    sonarAviso() // confirmación audible de que funciona
  }, [])

  const desactivar = useCallback(() => {
    setActivos(false)
    try { localStorage.setItem(CLAVE_PREF, 'off') } catch { /* ignorar */ }
  }, [])

  // Si ya estaban activados en una visita anterior, desbloquear el audio en el primer clic
  useEffect(() => {
    if (!activos) return
    const unlock = () => { desbloquearAudio(); document.removeEventListener('click', unlock) }
    document.addEventListener('click', unlock)
    return () => document.removeEventListener('click', unlock)
  }, [activos])

  return { activos, activar, desactivar, permisoNotif, nuevasSinVer }
}
